<script setup>
import { ref, computed, watch } from 'vue';
import { useI18n } from 'vue-i18n';

const { t } = useI18n();

const props = defineProps({
  isOpen: { type: Boolean, default: false },
  collections: { type: Array, default: () => [] },
  botId: { type: [Number, String], default: null },
  source: { type: Object, default: null },
});

const emit = defineEmits(['create', 'update', 'close']);

const isEditMode = computed(() => !!props.source);

const currentStep = ref(1);
const TOTAL_STEPS = 5;

// Step 1
const apiUrl = ref('');
const apiMethod = ref('GET');
const apiHeaders = ref([{ key: '', value: '' }]);
const apiBody = ref('');

// Step 2
const authType = ref('none');
const bearerToken = ref('');
const basicUsername = ref('');
const basicPassword = ref('');
const apiKeyHeader = ref('X-API-Key');
const apiKeyValue = ref('');

// Step 3
const testLoading = ref(false);
const testResult = ref(null);

// Step 4
const extractionTab = ref('preview');
const extractionPrompt = ref('');
const extractionConfig = ref(null);
const extractionConfigText = ref('');
const generatingExtraction = ref(false);
const extractionError = ref('');
const previewRecords = ref([]);
const previewTotal = ref(0);
const previewLoading = ref(false);

// Step 5
const collection = ref('');
const useNewCollection = ref(false);
const newCollection = ref('');
const syncInterval = ref(0);
const saving = ref(false);

const finalCollection = computed(() => {
  if (useNewCollection.value) return newCollection.value.trim();
  return collection.value;
});

const stepLabels = computed(() => [
  t('OMNI.DATA_SOURCES.API_SOURCE.STEP_CONFIG'),
  t('OMNI.DATA_SOURCES.API_SOURCE.STEP_AUTH'),
  t('OMNI.DATA_SOURCES.API_SOURCE.STEP_TEST'),
  t('OMNI.DATA_SOURCES.API_SOURCE.STEP_EXTRACT'),
  t('OMNI.DATA_SOURCES.API_SOURCE.STEP_CONFIRM'),
]);

const canProceedStep1 = computed(() => apiUrl.value.trim().length > 0);
const canProceedStep3 = computed(() => isEditMode.value || testResult.value?.success === true);
const canProceedStep4 = computed(() => extractionConfig.value !== null);

const canProceed = computed(() => {
  if (currentStep.value === 1) return canProceedStep1.value;
  if (currentStep.value === 3) return canProceedStep3.value;
  if (currentStep.value === 4) return canProceedStep4.value;
  return true;
});

const populateFromSource = source => {
  if (!source) return;
  apiUrl.value = source.api_url || '';
  apiMethod.value = source.api_method || 'GET';
  const headersObj = source.api_headers || {};
  const headersArr = Object.entries(headersObj).map(([key, value]) => ({ key, value }));
  apiHeaders.value = headersArr.length > 0 ? headersArr : [{ key: '', value: '' }];
  apiBody.value = source.api_body || '';
  authType.value = source.api_auth_type || 'none';
  const authConfig = source.api_auth_config || {};
  bearerToken.value = authConfig.token || '';
  basicUsername.value = authConfig.username || '';
  basicPassword.value = authConfig.password || '';
  apiKeyHeader.value = authConfig.header || 'X-API-Key';
  apiKeyValue.value = authConfig.value || '';
  if (source.extraction_config) {
    extractionConfig.value = source.extraction_config;
    extractionConfigText.value = JSON.stringify(source.extraction_config, null, 2);
  }
  extractionPrompt.value = source.extraction_prompt || '';
  collection.value = source.collection || '';
  useNewCollection.value = false;
  newCollection.value = '';
  syncInterval.value = source.sync_enabled ? (source.sync_interval_minutes || 0) : 0;
};

watch(() => props.isOpen, val => {
  if (val) {
    if (props.source) {
      populateFromSource(props.source);
    } else {
      resetForm();
    }
    currentStep.value = 1;
    testResult.value = null;
    previewRecords.value = [];
    previewTotal.value = 0;
    extractionTab.value = 'preview';
  } else {
    resetForm();
  }
});

watch(extractionConfigText, val => {
  try {
    extractionConfig.value = JSON.parse(val);
    extractionError.value = '';
  } catch {
    // keep last valid config
  }
});

const resetForm = () => {
  currentStep.value = 1;
  apiUrl.value = '';
  apiMethod.value = 'GET';
  apiHeaders.value = [{ key: '', value: '' }];
  apiBody.value = '';
  authType.value = 'none';
  bearerToken.value = '';
  basicUsername.value = '';
  basicPassword.value = '';
  apiKeyHeader.value = 'X-API-Key';
  apiKeyValue.value = '';
  testLoading.value = false;
  testResult.value = null;
  extractionTab.value = 'preview';
  extractionPrompt.value = '';
  extractionConfig.value = null;
  extractionConfigText.value = '';
  generatingExtraction.value = false;
  extractionError.value = '';
  previewRecords.value = [];
  previewTotal.value = 0;
  collection.value = '';
  useNewCollection.value = false;
  newCollection.value = '';
  syncInterval.value = 0;
  saving.value = false;
};

const buildPayload = () => {
  const headers = {};
  apiHeaders.value.forEach(({ key, value }) => {
    if (key.trim()) headers[key.trim()] = value;
  });
  return {
    api_url: apiUrl.value.trim(),
    api_method: apiMethod.value,
    api_headers: headers,
    api_body: apiBody.value.trim() || null,
    api_auth_type: authType.value === 'none' ? null : authType.value,
    api_auth_config: buildAuthConfig(),
    extraction_config: extractionConfig.value,
    extraction_prompt: extractionPrompt.value.trim() || null,
    collection: finalCollection.value || null,
    sync_enabled: syncInterval.value > 0,
    sync_interval_minutes: syncInterval.value > 0 ? syncInterval.value : null,
  };
};

const buildAuthConfig = () => {
  switch (authType.value) {
    case 'bearer': return { token: bearerToken.value };
    case 'basic': return { username: basicUsername.value, password: basicPassword.value };
    case 'api_key': return { header: apiKeyHeader.value, key: apiKeyHeader.value, value: apiKeyValue.value };
    default: return {};
  }
};

const buildTestPayload = () => {
  const headers = {};
  apiHeaders.value.forEach(({ key, value }) => {
    if (key.trim()) headers[key.trim()] = value;
  });
  return {
    api_url: apiUrl.value.trim(),
    api_method: apiMethod.value,
    api_headers: headers,
    api_body: apiBody.value.trim() || null,
    api_auth_type: authType.value === 'none' ? null : authType.value,
    api_auth_config: buildAuthConfig(),
  };
};

const handleTestApi = () => {
  testLoading.value = true;
  testResult.value = null;
  emit('test-api', buildTestPayload(), result => {
    testResult.value = result;
    testLoading.value = false;
  });
};

// In edit mode, run the test and then generate in sequence
const testAndGenerate = () => {
  if (!extractionPrompt.value.trim()) return;
  generatingExtraction.value = true;
  extractionError.value = '';

  const doGenerate = () => {
    const sampleJson = JSON.stringify(testResult.value.response);
    emit('generate-extraction', { sampleJson, extractionPrompt: extractionPrompt.value }, result => {
      generatingExtraction.value = false;
      if (result?.error) {
        extractionError.value = result.error;
      } else if (result?.extraction_config) {
        extractionConfig.value = result.extraction_config;
        extractionConfigText.value = JSON.stringify(result.extraction_config, null, 2);
        extractionTab.value = 'preview';
      }
    });
  };

  if (testResult.value?.response) {
    // Already have a test result — generate directly
    doGenerate();
    return;
  }

  // Need to run a test first
  emit('test-api', buildTestPayload(), result => {
    testResult.value = result;
    if (!result?.success) {
      generatingExtraction.value = false;
      extractionError.value = result?.error || t('OMNI.DATA_SOURCES.API_SOURCE.TEST_FAILED');
      return;
    }
    doGenerate();
  });
};

const handleGenerateExtraction = () => {
  if (!testResult.value?.response) return;
  generatingExtraction.value = true;
  extractionError.value = '';
  const sampleJson = JSON.stringify(testResult.value.response);
  emit('generate-extraction', { sampleJson, extractionPrompt: extractionPrompt.value }, result => {
    generatingExtraction.value = false;
    if (result?.error) {
      extractionError.value = result.error;
    } else if (result?.extraction_config) {
      extractionConfig.value = result.extraction_config;
      extractionConfigText.value = JSON.stringify(result.extraction_config, null, 2);
      extractionTab.value = 'preview';
    }
  });
};

const handlePreviewExtraction = () => {
  if (!testResult.value?.response || !extractionConfig.value) return;
  previewLoading.value = true;
  const sampleJson = JSON.stringify(testResult.value.response);
  emit('preview-extraction', { sampleJson, extractionConfig: extractionConfig.value }, result => {
    previewLoading.value = false;
    if (result?.records) {
      previewRecords.value = result.records;
      previewTotal.value = result.total_extracted || result.records.length;
    }
  });
};

const handleAddHeader = () => apiHeaders.value.push({ key: '', value: '' });
const handleRemoveHeader = index => apiHeaders.value.splice(index, 1);
const nextStep = () => { if (currentStep.value < TOTAL_STEPS) currentStep.value++; };
const prevStep = () => { if (currentStep.value > 1) currentStep.value--; };

const handleSubmit = () => {
  if (!canProceedStep4.value) return;
  saving.value = true;
  const payload = buildPayload();
  if (isEditMode.value) {
    emit('update', { id: props.source.id, ...payload }, () => { saving.value = false; });
  } else {
    emit('create', payload, () => { saving.value = false; });
  }
};

const formatBytes = bytes => {
  if (!bytes) return '0 B';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const syncIntervalOptions = [
  { label: t('OMNI.DATA_SOURCES.API_SOURCE.SYNC_DISABLED'), value: 0 },
  { label: t('OMNI.DATA_SOURCES.API_SOURCE.SYNC_15MIN'), value: 15 },
  { label: t('OMNI.DATA_SOURCES.API_SOURCE.SYNC_1H'), value: 60 },
  { label: t('OMNI.DATA_SOURCES.API_SOURCE.SYNC_6H'), value: 360 },
  { label: t('OMNI.DATA_SOURCES.API_SOURCE.SYNC_12H'), value: 720 },
  { label: t('OMNI.DATA_SOURCES.API_SOURCE.SYNC_24H'), value: 1440 },
];
</script>

<template>
  <div
    v-if="isOpen"
    class="fixed inset-0 z-50 flex items-center justify-center"
  >
    <!-- Backdrop -->
    <div
      class="absolute inset-0 bg-black/50 backdrop-blur-sm"
      @click="emit('close')"
    />

    <!-- Modal -->
    <div
      class="relative bg-white dark:bg-slate-800 rounded-lg shadow-xl w-full max-w-2xl mx-4 overflow-hidden flex flex-col max-h-[90vh]"
    >
      <!-- Header -->
      <div class="px-6 py-4 border-b border-slate-200 dark:border-slate-700 flex items-center justify-between">
        <div>
          <h2 class="text-lg font-semibold text-slate-900 dark:text-slate-100">
            {{ isEditMode ? t('OMNI.DATA_SOURCES.API_SOURCE.EDIT_TITLE') : t('OMNI.DATA_SOURCES.API_SOURCE.TITLE') }}
          </h2>
          <p v-if="isEditMode" class="text-xs text-slate-500 dark:text-slate-400 truncate max-w-md mt-0.5">
            {{ source?.api_url }}
          </p>
        </div>
        <button
          class="text-slate-500 hover:text-slate-700 dark:hover:text-slate-300"
          @click="emit('close')"
        >
          <span class="i-lucide-x w-5 h-5" />
        </button>
      </div>

      <!-- Stepper -->
      <div class="px-6 py-3 border-b border-slate-200 dark:border-slate-700 overflow-x-auto">
        <div class="flex items-center min-w-max">
          <template v-for="(label, index) in stepLabels" :key="index">
            <div class="flex items-center gap-1.5">
              <div
                class="flex h-6 w-6 items-center justify-center rounded-full text-xs font-bold transition-all flex-shrink-0"
                :class="currentStep > index + 1
                  ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300'
                  : currentStep === index + 1
                    ? 'bg-woot-500 text-white'
                    : 'bg-slate-100 dark:bg-slate-700 text-slate-400 dark:text-slate-500'"
              >
                <span v-if="currentStep > index + 1" class="i-lucide-check w-3.5 h-3.5" />
                <span v-else>{{ index + 1 }}</span>
              </div>
              <span
                class="text-xs font-medium hidden sm:inline whitespace-nowrap"
                :class="currentStep === index + 1
                  ? 'text-slate-900 dark:text-slate-100'
                  : currentStep > index + 1
                    ? 'text-green-700 dark:text-green-300'
                    : 'text-slate-400 dark:text-slate-500'"
              >{{ label }}</span>
            </div>
            <div
              v-if="index < stepLabels.length - 1"
              class="flex-1 h-px mx-2 min-w-4"
              :class="currentStep > index + 1 ? 'bg-green-200 dark:bg-green-800' : 'bg-slate-200 dark:bg-slate-700'"
            />
          </template>
        </div>
      </div>

      <!-- Step Content -->
      <div class="flex-1 overflow-y-auto p-6 space-y-4">

        <!-- ═══ Step 1: API Config ═══ -->
        <template v-if="currentStep === 1">
          <div>
            <label class="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
              {{ t('OMNI.DATA_SOURCES.API_SOURCE.URL_LABEL') }}
            </label>
            <input
              v-model="apiUrl"
              type="url"
              class="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-md bg-white dark:bg-slate-700 text-slate-900 dark:text-slate-100"
              :placeholder="t('OMNI.DATA_SOURCES.API_SOURCE.URL_PLACEHOLDER')"
            />
          </div>

          <div>
            <label class="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
              {{ t('OMNI.DATA_SOURCES.API_SOURCE.METHOD_LABEL') }}
            </label>
            <div class="flex gap-2">
              <button
                v-for="m in ['GET', 'POST', 'PUT', 'PATCH']"
                :key="m"
                type="button"
                class="px-3 py-1.5 text-xs font-medium rounded-md transition-colors"
                :class="apiMethod === m
                  ? 'bg-woot-500 text-white'
                  : 'bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-slate-600'"
                @click="apiMethod = m"
              >
                {{ m }}
              </button>
            </div>
          </div>

          <!-- Headers -->
          <div>
            <label class="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
              {{ t('OMNI.DATA_SOURCES.API_SOURCE.HEADERS_LABEL') }}
              <span class="text-slate-400 font-normal ml-1">({{ t('OMNI.DATA_SOURCES.OPTIONAL') }})</span>
            </label>
            <div class="space-y-2">
              <div v-for="(header, index) in apiHeaders" :key="index" class="flex gap-2">
                <input
                  v-model="header.key"
                  type="text"
                  class="flex-1 px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-md bg-white dark:bg-slate-700 text-slate-900 dark:text-slate-100"
                  :placeholder="t('OMNI.DATA_SOURCES.API_SOURCE.HEADERS_KEY_PLACEHOLDER')"
                />
                <input
                  v-model="header.value"
                  type="text"
                  class="flex-1 px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-md bg-white dark:bg-slate-700 text-slate-900 dark:text-slate-100"
                  :placeholder="t('OMNI.DATA_SOURCES.API_SOURCE.HEADERS_VALUE_PLACEHOLDER')"
                />
                <button
                  type="button"
                  class="p-2 text-slate-400 hover:text-red-500 rounded-md hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors"
                  @click="handleRemoveHeader(index)"
                >
                  <span class="i-lucide-trash-2 w-3.5 h-3.5" />
                </button>
              </div>
            </div>
            <button
              type="button"
              class="mt-2 inline-flex items-center gap-1.5 text-xs font-medium text-woot-600 dark:text-woot-400 hover:underline"
              @click="handleAddHeader"
            >
              <span class="i-lucide-plus w-3.5 h-3.5" />
              {{ t('OMNI.DATA_SOURCES.API_SOURCE.ADD_HEADER') }}
            </button>
          </div>

          <!-- Body -->
          <div v-if="['POST', 'PUT', 'PATCH'].includes(apiMethod)">
            <label class="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
              {{ t('OMNI.DATA_SOURCES.API_SOURCE.BODY_LABEL') }}
            </label>
            <textarea
              v-model="apiBody"
              rows="4"
              class="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-md bg-white dark:bg-slate-700 text-slate-900 dark:text-slate-100 font-mono text-sm"
              :placeholder="t('OMNI.DATA_SOURCES.API_SOURCE.BODY_PLACEHOLDER')"
            />
          </div>
        </template>

        <!-- ═══ Step 2: Authentication ═══ -->
        <template v-else-if="currentStep === 2">
          <div>
            <label class="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
              {{ t('OMNI.DATA_SOURCES.API_SOURCE.AUTH_LABEL') }}
            </label>
            <div class="grid grid-cols-2 gap-2 sm:grid-cols-4">
              <button
                v-for="opt in [
                  { value: 'none', label: t('OMNI.DATA_SOURCES.API_SOURCE.AUTH_NONE'), icon: 'i-lucide-lock-open' },
                  { value: 'bearer', label: t('OMNI.DATA_SOURCES.API_SOURCE.AUTH_BEARER'), icon: 'i-lucide-key' },
                  { value: 'basic', label: t('OMNI.DATA_SOURCES.API_SOURCE.AUTH_BASIC'), icon: 'i-lucide-user' },
                  { value: 'api_key', label: t('OMNI.DATA_SOURCES.API_SOURCE.AUTH_API_KEY'), icon: 'i-lucide-hash' },
                ]"
                :key="opt.value"
                type="button"
                class="flex flex-col items-center gap-1.5 p-3 rounded-lg border transition-colors text-center"
                :class="authType === opt.value
                  ? 'border-woot-500 bg-woot-50 dark:bg-woot-900/20 text-woot-600 dark:text-woot-400'
                  : 'border-slate-200 dark:border-slate-600 text-slate-600 dark:text-slate-300 hover:border-slate-300 dark:hover:border-slate-500'"
                @click="authType = opt.value"
              >
                <span :class="opt.icon" class="w-4 h-4" />
                <span class="text-xs font-medium">{{ opt.label }}</span>
              </button>
            </div>
          </div>

          <!-- Bearer -->
          <div v-if="authType === 'bearer'" class="p-4 bg-slate-50 dark:bg-slate-700/50 rounded-md space-y-3">
            <label class="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
              {{ t('OMNI.DATA_SOURCES.API_SOURCE.BEARER_TOKEN_LABEL') }}
            </label>
            <input
              v-model="bearerToken"
              type="password"
              class="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-md bg-white dark:bg-slate-700 text-slate-900 dark:text-slate-100"
              :placeholder="t('OMNI.DATA_SOURCES.API_SOURCE.BEARER_TOKEN_PLACEHOLDER')"
            />
          </div>

          <!-- Basic -->
          <div v-if="authType === 'basic'" class="p-4 bg-slate-50 dark:bg-slate-700/50 rounded-md space-y-3">
            <div>
              <label class="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                {{ t('OMNI.DATA_SOURCES.API_SOURCE.BASIC_USERNAME_LABEL') }}
              </label>
              <input v-model="basicUsername" type="text" class="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-md bg-white dark:bg-slate-700 text-slate-900 dark:text-slate-100" :placeholder="t('OMNI.DATA_SOURCES.API_SOURCE.BASIC_USERNAME_PLACEHOLDER')" />
            </div>
            <div>
              <label class="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                {{ t('OMNI.DATA_SOURCES.API_SOURCE.BASIC_PASSWORD_LABEL') }}
              </label>
              <input v-model="basicPassword" type="password" class="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-md bg-white dark:bg-slate-700 text-slate-900 dark:text-slate-100" :placeholder="t('OMNI.DATA_SOURCES.API_SOURCE.BASIC_PASSWORD_PLACEHOLDER')" />
            </div>
          </div>

          <!-- API Key -->
          <div v-if="authType === 'api_key'" class="p-4 bg-slate-50 dark:bg-slate-700/50 rounded-md space-y-3">
            <div>
              <label class="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                {{ t('OMNI.DATA_SOURCES.API_SOURCE.API_KEY_HEADER_LABEL') }}
              </label>
              <input v-model="apiKeyHeader" type="text" class="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-md bg-white dark:bg-slate-700 text-slate-900 dark:text-slate-100" :placeholder="t('OMNI.DATA_SOURCES.API_SOURCE.API_KEY_HEADER_PLACEHOLDER')" />
            </div>
            <div>
              <label class="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                {{ t('OMNI.DATA_SOURCES.API_SOURCE.API_KEY_VALUE_LABEL') }}
              </label>
              <input v-model="apiKeyValue" type="password" class="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-md bg-white dark:bg-slate-700 text-slate-900 dark:text-slate-100" :placeholder="t('OMNI.DATA_SOURCES.API_SOURCE.API_KEY_VALUE_PLACEHOLDER')" />
            </div>
          </div>
        </template>

        <!-- ═══ Step 3: Test ═══ -->
        <template v-else-if="currentStep === 3">
          <!-- Edit mode hint -->
          <div v-if="isEditMode" class="flex items-start gap-3 p-3 border border-yellow-200 dark:border-yellow-800 rounded-md bg-yellow-50 dark:bg-yellow-900/20">
            <span class="i-lucide-info text-yellow-600 dark:text-yellow-400 flex-shrink-0 mt-0.5 w-4 h-4" />
            <p class="text-xs text-yellow-700 dark:text-yellow-300">
              {{ t('OMNI.DATA_SOURCES.API_SOURCE.RETEST_HINT') }}
            </p>
          </div>

          <div class="p-4 bg-slate-50 dark:bg-slate-700/50 rounded-md">
            <div class="flex items-center gap-2 mb-3">
              <span class="px-2 py-0.5 text-xs font-bold rounded bg-woot-50 dark:bg-woot-900/20 text-woot-600 dark:text-woot-400">
                {{ apiMethod }}
              </span>
              <code class="text-sm text-slate-700 dark:text-slate-300 truncate">{{ apiUrl }}</code>
            </div>
            <button
              type="button"
              class="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-md transition-colors"
              :class="testLoading
                ? 'bg-slate-200 dark:bg-slate-600 text-slate-500 dark:text-slate-400 cursor-not-allowed'
                : 'bg-woot-500 hover:bg-woot-600 text-white'"
              :disabled="testLoading"
              @click="handleTestApi"
            >
              <span :class="testLoading ? 'i-lucide-loader-2 w-4 h-4 animate-spin' : 'i-lucide-play w-4 h-4'" />
              {{ testLoading ? t('OMNI.DATA_SOURCES.API_SOURCE.TESTING') : t('OMNI.DATA_SOURCES.API_SOURCE.TEST_BUTTON') }}
            </button>
          </div>

          <!-- Test result -->
          <template v-if="testResult">
            <div
              class="flex items-center gap-3 p-3 rounded-md border"
              :class="testResult.success
                ? 'bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800'
                : 'bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800'"
            >
              <span
                class="w-5 h-5 flex-shrink-0"
                :class="testResult.success ? 'i-lucide-check-circle text-green-500' : 'i-lucide-x-circle text-red-500'"
              />
              <div class="flex-1 min-w-0">
                <p
                  class="text-sm font-medium"
                  :class="testResult.success ? 'text-green-700 dark:text-green-300' : 'text-red-700 dark:text-red-300'"
                >
                  {{ testResult.success ? t('OMNI.DATA_SOURCES.API_SOURCE.TEST_SUCCESS') : t('OMNI.DATA_SOURCES.API_SOURCE.TEST_FAILED') }}
                </p>
                <p v-if="testResult.error" class="text-xs text-red-600 dark:text-red-400 mt-0.5">{{ testResult.error }}</p>
              </div>
              <div v-if="testResult.success" class="flex gap-3 text-xs text-slate-500 dark:text-slate-400 flex-shrink-0">
                <span>{{ testResult.elapsed_ms }}ms</span>
                <span>{{ formatBytes(testResult.response_size) }}</span>
              </div>
            </div>

            <div v-if="testResult.response" class="rounded-md border border-slate-200 dark:border-slate-700 overflow-hidden">
              <div class="px-3 py-2 bg-slate-50 dark:bg-slate-700/50 border-b border-slate-200 dark:border-slate-700">
                <span class="text-xs font-semibold text-slate-600 dark:text-slate-400 uppercase tracking-wider">
                  {{ t('OMNI.DATA_SOURCES.API_SOURCE.RESPONSE_PREVIEW') }}
                </span>
              </div>
              <pre class="p-3 bg-slate-50 dark:bg-slate-900 text-xs text-slate-700 dark:text-slate-300 overflow-auto max-h-48 font-mono">{{ JSON.stringify(testResult.response, null, 2).slice(0, 3000) }}</pre>
            </div>
          </template>
        </template>

        <!-- ═══ Step 4: Extraction ═══ -->
        <template v-else-if="currentStep === 4">
          <div>
            <label class="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
              {{ t('OMNI.DATA_SOURCES.API_SOURCE.EXTRACTION_PROMPT_LABEL') }}
            </label>
            <textarea
              v-model="extractionPrompt"
              rows="2"
              class="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-md bg-white dark:bg-slate-700 text-slate-900 dark:text-slate-100"
              :placeholder="t('OMNI.DATA_SOURCES.API_SOURCE.EXTRACTION_PROMPT_PLACEHOLDER')"
            />
            <p class="mt-1 text-xs text-slate-500">
              {{ t('OMNI.DATA_SOURCES.API_SOURCE.EXTRACTION_HINT') }}
            </p>
          </div>

          <div class="flex gap-2">
            <!-- When there's already a test result, generate directly -->
            <button
              v-if="testResult?.response"
              type="button"
              class="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-md transition-colors"
              :class="generatingExtraction || !extractionPrompt.trim()
                ? 'bg-slate-200 dark:bg-slate-600 text-slate-400 dark:text-slate-500 cursor-not-allowed'
                : 'bg-woot-500 hover:bg-woot-600 text-white'"
              :disabled="generatingExtraction || !extractionPrompt.trim()"
              @click="handleGenerateExtraction"
            >
              <span :class="generatingExtraction ? 'i-lucide-loader-2 w-4 h-4 animate-spin' : 'i-lucide-sparkles w-4 h-4'" />
              {{ generatingExtraction ? t('OMNI.DATA_SOURCES.API_SOURCE.GENERATING') : t('OMNI.DATA_SOURCES.API_SOURCE.GENERATE_EXTRACTION') }}
            </button>

            <!-- When there's no test result, test + generate in sequence -->
            <button
              v-else
              type="button"
              class="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-md transition-colors"
              :class="generatingExtraction || !extractionPrompt.trim()
                ? 'bg-slate-200 dark:bg-slate-600 text-slate-400 dark:text-slate-500 cursor-not-allowed'
                : 'bg-woot-500 hover:bg-woot-600 text-white'"
              :disabled="generatingExtraction || !extractionPrompt.trim()"
              @click="testAndGenerate"
            >
              <span :class="generatingExtraction ? 'i-lucide-loader-2 w-4 h-4 animate-spin' : 'i-lucide-sparkles w-4 h-4'" />
              {{ generatingExtraction ? t('OMNI.DATA_SOURCES.API_SOURCE.GENERATING') : t('OMNI.DATA_SOURCES.API_SOURCE.GENERATE_EXTRACTION') }}
            </button>

            <button
              v-if="extractionConfig && testResult?.response"
              type="button"
              class="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-slate-700 dark:text-slate-300 bg-slate-100 dark:bg-slate-700 hover:bg-slate-200 dark:hover:bg-slate-600 rounded-md transition-colors"
              :disabled="previewLoading"
              @click="handlePreviewExtraction"
            >
              <span :class="previewLoading ? 'i-lucide-loader-2 w-4 h-4 animate-spin' : 'i-lucide-eye w-4 h-4'" />
              {{ t('OMNI.DATA_SOURCES.API_SOURCE.PREVIEW_EXTRACTION') }}
            </button>
          </div>

          <!-- Extraction error -->
          <div
            v-if="extractionError"
            class="p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-md"
          >
            <div class="flex items-center gap-2">
              <span class="i-lucide-x-circle w-4 h-4 text-red-500" />
              <span class="text-sm text-red-700 dark:text-red-300">{{ extractionError }}</span>
            </div>
          </div>

          <!-- Tabs: Preview / Advanced -->
          <div v-if="extractionConfig" class="rounded-md border border-slate-200 dark:border-slate-700 overflow-hidden">
            <!-- Tab headers -->
            <div class="flex border-b border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-700/50">
              <button
                type="button"
                class="px-4 py-2 text-xs font-medium border-b-2 transition-colors -mb-px"
                :class="extractionTab === 'preview'
                  ? 'border-woot-500 text-woot-600 dark:text-woot-400'
                  : 'border-transparent text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-200'"
                @click="extractionTab = 'preview'"
              >
                <span class="flex items-center gap-1.5">
                  <span class="i-lucide-table-2 w-3.5 h-3.5" />
                  {{ t('OMNI.DATA_SOURCES.API_SOURCE.TAB_PREVIEW') }}
                  <span v-if="previewTotal > 0" class="px-1.5 py-0.5 text-[10px] rounded-full bg-woot-100 dark:bg-woot-900/30 text-woot-700 dark:text-woot-300 font-bold">
                    {{ previewTotal }}
                  </span>
                </span>
              </button>
              <button
                type="button"
                class="px-4 py-2 text-xs font-medium border-b-2 transition-colors -mb-px"
                :class="extractionTab === 'advanced'
                  ? 'border-woot-500 text-woot-600 dark:text-woot-400'
                  : 'border-transparent text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-200'"
                @click="extractionTab = 'advanced'"
              >
                <span class="flex items-center gap-1.5">
                  <span class="i-lucide-code-2 w-3.5 h-3.5" />
                  {{ t('OMNI.DATA_SOURCES.API_SOURCE.TAB_ADVANCED') }}
                </span>
              </button>
            </div>

            <!-- Preview tab -->
            <div v-if="extractionTab === 'preview'">
              <div v-if="previewRecords.length === 0" class="flex flex-col items-center justify-center py-8 text-center">
                <span class="i-lucide-table-2 text-3xl text-slate-300 dark:text-slate-600 mb-2" />
                <p class="text-xs text-slate-500 dark:text-slate-400">
                  Click "{{ t('OMNI.DATA_SOURCES.API_SOURCE.PREVIEW_EXTRACTION') }}" to see extracted records
                </p>
              </div>
              <div v-else>
                <div class="px-3 py-2 bg-green-50 dark:bg-green-900/20 border-b border-green-100 dark:border-green-900/30 flex items-center gap-2">
                  <span class="i-lucide-check-circle text-green-500 w-3.5 h-3.5" />
                  <span class="text-xs font-medium text-green-700 dark:text-green-300">
                    {{ t('OMNI.DATA_SOURCES.API_SOURCE.PREVIEW_TOTAL', { count: previewTotal }) }}
                  </span>
                </div>
                <div class="overflow-auto max-h-48">
                  <table class="w-full text-xs">
                    <tbody>
                      <tr
                        v-for="(record, i) in previewRecords.slice(0, 5)"
                        :key="i"
                        class="border-b border-slate-100 dark:border-slate-700 last:border-0"
                      >
                        <td class="px-3 py-2 text-slate-400 w-6 align-top font-mono">{{ i + 1 }}</td>
                        <td class="px-3 py-2">
                          <div class="flex flex-wrap gap-1.5">
                            <template v-for="(val, key) in record.data" :key="key">
                              <span v-if="key !== 'type'" class="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-slate-100 dark:bg-slate-700 text-slate-700 dark:text-slate-300">
                                <span class="text-slate-400 dark:text-slate-500 font-medium">{{ key }}:</span>
                                <span class="truncate max-w-[160px]">{{ val }}</span>
                              </span>
                            </template>
                          </div>
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            </div>

            <!-- Advanced tab -->
            <div v-else-if="extractionTab === 'advanced'" class="p-4">
              <p class="text-xs text-slate-500 dark:text-slate-400 mb-2">
                {{ t('OMNI.DATA_SOURCES.API_SOURCE.EXTRACTION_CONFIG_HINT') }}
              </p>
              <textarea
                v-model="extractionConfigText"
                rows="10"
                class="w-full px-3 py-2 text-xs border border-slate-300 dark:border-slate-600 rounded-md bg-slate-50 dark:bg-slate-900 text-slate-700 dark:text-slate-300 font-mono"
                spellcheck="false"
              />
            </div>
          </div>
        </template>

        <!-- ═══ Step 5: Confirm ═══ -->
        <template v-else-if="currentStep === 5">
          <!-- Summary -->
          <div class="p-4 bg-slate-50 dark:bg-slate-700/50 rounded-md space-y-2">
            <h3 class="text-sm font-semibold text-slate-700 dark:text-slate-300 mb-2">
              {{ t('OMNI.DATA_SOURCES.API_SOURCE.CONFIRM_TITLE') }}
            </h3>
            <div class="flex items-start justify-between gap-4 text-sm">
              <span class="text-slate-500 dark:text-slate-400">{{ t('OMNI.DATA_SOURCES.API_SOURCE.CONFIRM_URL') }}</span>
              <code class="text-slate-800 dark:text-slate-200 truncate text-xs">{{ apiUrl }}</code>
            </div>
            <div class="flex items-center justify-between text-sm">
              <span class="text-slate-500 dark:text-slate-400">{{ t('OMNI.DATA_SOURCES.API_SOURCE.CONFIRM_METHOD') }}</span>
              <span class="font-bold text-woot-600 dark:text-woot-400">{{ apiMethod }}</span>
            </div>
            <div class="flex items-center justify-between text-sm">
              <span class="text-slate-500 dark:text-slate-400">{{ t('OMNI.DATA_SOURCES.API_SOURCE.CONFIRM_AUTH') }}</span>
              <span class="text-slate-700 dark:text-slate-300 capitalize">{{ authType === 'none' ? 'None' : authType }}</span>
            </div>
            <div v-if="extractionConfig?.fields" class="flex items-start justify-between gap-4 text-sm">
              <span class="text-slate-500 dark:text-slate-400">{{ t('OMNI.DATA_SOURCES.API_SOURCE.CONFIRM_FIELDS') }}</span>
              <span class="text-slate-700 dark:text-slate-300 text-xs text-right">{{ extractionConfig.fields.map(f => f.name).join(', ') }}</span>
            </div>
          </div>

          <!-- Collection -->
          <div>
            <label class="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
              {{ t('OMNI.DATA_SOURCES.API_SOURCE.COLLECTION_LABEL') }}
              <span class="text-slate-400 font-normal ml-1">({{ t('OMNI.DATA_SOURCES.OPTIONAL') }})</span>
            </label>
            <div class="flex gap-2 mb-2">
              <button
                type="button"
                class="px-3 py-1.5 text-xs font-medium rounded-md transition-colors"
                :class="!useNewCollection
                  ? 'bg-woot-500 text-white'
                  : 'bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-slate-600'"
                @click="useNewCollection = false"
              >
                {{ t('OMNI.DATA_SOURCES.EXISTING_COLLECTION') }}
              </button>
              <button
                type="button"
                class="px-3 py-1.5 text-xs font-medium rounded-md transition-colors"
                :class="useNewCollection
                  ? 'bg-woot-500 text-white'
                  : 'bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-slate-600'"
                @click="useNewCollection = true"
              >
                {{ t('OMNI.DATA_SOURCES.NEW_COLLECTION') }}
              </button>
            </div>
            <select
              v-if="!useNewCollection"
              v-model="collection"
              class="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-md bg-white dark:bg-slate-700 text-slate-900 dark:text-slate-100"
            >
              <option value="">{{ t('OMNI.DATA_SOURCES.AUTO_DETECT') }}</option>
              <option v-for="col in collections" :key="col" :value="col">{{ col }}</option>
            </select>
            <input
              v-else
              v-model="newCollection"
              type="text"
              class="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-md bg-white dark:bg-slate-700 text-slate-900 dark:text-slate-100"
              :placeholder="t('OMNI.DATA_SOURCES.NEW_COLLECTION_PLACEHOLDER')"
            />
          </div>

          <!-- Sync schedule -->
          <div>
            <label class="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
              {{ t('OMNI.DATA_SOURCES.API_SOURCE.SYNC_LABEL') }}
            </label>
            <select
              v-model="syncInterval"
              class="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-md bg-white dark:bg-slate-700 text-slate-900 dark:text-slate-100"
            >
              <option v-for="opt in syncIntervalOptions" :key="opt.value" :value="opt.value">
                {{ opt.label }}
              </option>
            </select>
            <p v-if="syncInterval > 0" class="mt-1 text-xs text-woot-600 dark:text-woot-400 flex items-center gap-1">
              <span class="i-lucide-refresh-cw w-3 h-3" />
              Data will be automatically refreshed. Old entries will be replaced on each sync.
            </p>
          </div>
        </template>

      </div>

      <!-- Footer -->
      <div class="px-6 py-4 border-t border-slate-200 dark:border-slate-700 flex justify-between">
        <button
          v-if="currentStep > 1"
          type="button"
          class="px-4 py-2 text-sm font-medium text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-md transition-colors"
          @click="prevStep"
        >
          <span class="i-lucide-arrow-left w-3.5 h-3.5 mr-1 inline-block align-text-bottom" />
          {{ t('OMNI.DATA_SOURCES.BACK') }}
        </button>
        <div v-else />

        <div class="flex gap-2">
          <button
            type="button"
            class="px-4 py-2 text-sm font-medium text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-md transition-colors"
            @click="emit('close')"
          >
            {{ t('OMNI.DATA_SOURCES.CANCEL') }}
          </button>

          <button
            v-if="currentStep < TOTAL_STEPS"
            type="button"
            class="inline-flex items-center gap-1.5 px-4 py-2 text-sm font-medium text-white bg-woot-500 rounded-md hover:bg-woot-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            :disabled="!canProceed"
            @click="nextStep"
          >
            {{ t('OMNI.DATA_SOURCES.NEXT') }}
            <span class="i-lucide-arrow-right w-3.5 h-3.5" />
          </button>

          <button
            v-else
            type="button"
            class="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-woot-500 rounded-md hover:bg-woot-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            :disabled="saving || !canProceedStep4"
            @click="handleSubmit"
          >
            <span v-if="saving" class="i-lucide-loader-2 animate-spin w-4 h-4" />
            <span v-else class="i-lucide-check w-4 h-4" />
            {{ saving
              ? (isEditMode ? t('OMNI.DATA_SOURCES.API_SOURCE.UPDATING') : t('OMNI.DATA_SOURCES.API_SOURCE.CREATING'))
              : (isEditMode ? t('OMNI.DATA_SOURCES.API_SOURCE.UPDATE_SUBMIT') : t('OMNI.DATA_SOURCES.API_SOURCE.CONFIRM_SUBMIT'))
            }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>
